/**
 * VOSviewer's tick algorithm, ported from `com.vosviewer.Ticks`.
 *
 * Two jobs: snapping an auto-detected overlay range to round numbers, and
 * placing the labels on the colour bar.  Both are the same computation, so
 * they live together.
 *
 * The Java original does this in `BigDecimal` to keep 0.1 + 0.2 from turning
 * into 0.30000000000000004 in a tick label.  We stay in doubles and fix the
 * display end instead: `tickLabel` formats with exactly the number of fraction
 * digits the tick spacing implies, which rounds the noise away.  Tick *values*
 * can still carry float dust, so compare them with a tolerance if you ever
 * need to.
 */

const EPSILON = 1e-8;

/** Java's `Math.signum`, which returns 0 for 0 and NaN for NaN. */
function signum(v: number): number {
  return v > 0 ? 1 : v < 0 ? -1 : v;
}

/** `Ticks.floor` — a floor that tolerates a value a hair below an integer. */
function tolerantFloor(value: number): number {
  return Math.floor((1.0 + signum(value) * EPSILON) * value);
}

/** `Ticks.ceil` — a ceiling that tolerates a value a hair above an integer. */
function tolerantCeil(value: number): number {
  return Math.ceil((1.0 - signum(value) * EPSILON) * value);
}

/**
 * The nearest "nice" number: 1, 2, 5 or 10 times a power of ten.
 *
 * `round = false` takes the smallest nice value that is at least `value` and is
 * used for the range; `round = true` takes the nearest and is used for the tick
 * spacing.  The asymmetry is VOSviewer's and it is what stops a range of 0.9
 * from being drawn with a spacing of 1.
 */
function niceValue(value: number, round: boolean): number {
  if (!(value > 0) || !Number.isFinite(value)) return 0;
  const exponent = tolerantFloor(Math.log10(value));
  const fractionalPart = value / Math.pow(10.0, exponent);
  let nice: number;
  if (round) {
    nice = fractionalPart < 1.5 ? 1 : fractionalPart < 3.0 ? 2 : fractionalPart < 7.0 ? 5 : 10;
  } else {
    nice =
      fractionalPart <= 1.0 + EPSILON
        ? 1
        : fractionalPart <= 2.0 + EPSILON
          ? 2
          : fractionalPart <= 5.0 + EPSILON
            ? 5
            : 10;
  }
  return nice * Math.pow(10.0, exponent);
}

function tickDistance(minValue: number, maxValue: number, desiredNTicks: number): number {
  const range = niceValue(maxValue - minValue, false);
  return niceValue(range / (desiredNTicks - 1), true);
}

/** Snap a range minimum down to a round number. `Ticks.getNiceMinValue`. */
export function niceMinValue(minValue: number, maxValue: number, desiredNTicks = 5): number {
  if (minValue === maxValue) return minValue;
  const distance = tickDistance(minValue, maxValue, desiredNTicks);
  if (!(distance > 0)) return minValue;
  return Math.round(minValue / distance) * distance;
}

/** Snap a range maximum up to a round number. `Ticks.getNiceMaxValue`. */
export function niceMaxValue(minValue: number, maxValue: number, desiredNTicks = 5): number {
  if (minValue === maxValue) return maxValue;
  const distance = tickDistance(minValue, maxValue, desiredNTicks);
  if (!(distance > 0)) return maxValue;
  return Math.round(maxValue / distance) * distance;
}

export interface Ticks {
  readonly values: number[];
  readonly labels: string[];
  /** Fraction digits implied by the tick spacing; 0 when the spacing is >= 1. */
  readonly fractionDigits: number;
}

/**
 * Tick values and labels across `[minValue, maxValue]`.
 *
 * Returns the two endpoints when the range is degenerate, so a colour bar
 * always has something to draw.
 */
export function ticks(minValue: number, maxValue: number, desiredNTicks = 5): Ticks {
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue) || minValue === maxValue) {
    const digits = 2;
    return {
      values: [minValue],
      labels: [formatFixed(minValue, digits)],
      fractionDigits: digits,
    };
  }
  const distance = tickDistance(minValue, maxValue, desiredNTicks);
  if (!(distance > 0)) {
    return { values: [minValue, maxValue], labels: [String(minValue), String(maxValue)], fractionDigits: 0 };
  }
  const minTick = tolerantCeil(minValue / distance) * distance;
  const maxTick = tolerantFloor(maxValue / distance) * distance;
  const n = Math.max(1, Math.round((maxTick - minTick) / distance) + 1);
  const fractionDigits = Math.max(0, -tolerantFloor(Math.log10(distance)));
  const values: number[] = new Array<number>(n);
  const labels: string[] = new Array<string>(n);
  for (let i = 0; i < n; i += 1) {
    const v = minTick + i * distance;
    values[i] = v;
    labels[i] = formatFixed(v, fractionDigits);
  }
  return { values, labels, fractionDigits };
}

/** `Ticks.getLabelNormal`: fixed notation, US decimal point, no thousands grouping. */
export function formatFixed(value: number, fractionDigits: number): string {
  if (!Number.isFinite(value)) return '—';
  // toFixed caps at 100 digits and throws beyond that; the tick spacing never
  // gets anywhere near, but a caller-supplied range could.
  const digits = Math.min(20, Math.max(0, fractionDigits));
  const s = value.toFixed(digits);
  // toFixed renders -0 as "-0.00"; a legend showing "-0" looks like a bug.
  return s.replace(/^-(0(\.0*)?)$/, '$1');
}
